/**
 * Locale-aware formatting of the things a shop shows most: money, dates and
 * counts.
 *
 * All of it goes through `Intl`, which means the differences that actually
 * matter are handled for free — €1.234,56 in German against €1,234.56 in
 * English, ١٬٢٣٤ in Arabic, and the Indian lakh grouping of 1,23,456 in Hindi.
 * Formatting money by hand with `toFixed(2)` and a currency symbol gets every
 * one of those wrong, and gets symbol PLACEMENT wrong too: "1,50 €" in French
 * puts the symbol after the amount.
 */
const numberCache = new Map();
const dateCache = new Map();
const numberFormatter = (locale, options) => {
    const key = `${locale}:${JSON.stringify(options)}`;
    const existing = numberCache.get(key);
    if (existing) {
        return existing;
    }
    const created = new Intl.NumberFormat(locale, options);
    numberCache.set(key, created);
    return created;
};
const dateFormatter = (locale, options) => {
    const key = `${locale}:${JSON.stringify(options)}`;
    const existing = dateCache.get(key);
    if (existing) {
        return existing;
    }
    const created = new Intl.DateTimeFormat(locale, options);
    dateCache.set(key, created);
    return created;
};
/**
 * Money arrives as a decimal STRING, never a number — that is the whole reason
 * prices are stored as NUMERIC and transported as text. It is parsed to a float
 * only here, at the very last step before a human reads it, where a
 * sub-cent representation error cannot propagate into any further arithmetic.
 *
 * `Intl` also supplies the right number of decimal places per currency, so ¥
 * shows none and JOD shows three without a lookup table on our side.
 */
export const formatMoney = (amount, currencyCode, locale) => {
    const value = Number(amount);
    if (!Number.isFinite(value)) {
        return amount;
    }
    return numberFormatter(locale, { style: 'currency', currency: currencyCode }).format(value);
};
export const formatNumber = (value, locale) => numberFormatter(locale, {}).format(value);
export const formatPercent = (fraction, locale) => numberFormatter(locale, { style: 'percent', maximumFractionDigits: 1 }).format(fraction);
export const formatDate = (value, locale) => dateFormatter(locale, { dateStyle: 'medium' }).format(new Date(value));
export const formatDateTime = (value, locale) => dateFormatter(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
/**
 * "3 days ago", "in 2 hours".
 *
 * Uses `Intl.RelativeTimeFormat`, which conjugates correctly in languages where
 * the unit changes with the number — something string concatenation cannot do.
 */
export const formatRelativeTime = (value, locale) => {
    const then = new Date(value).getTime();
    const seconds = Math.round((then - Date.now()) / 1000);
    const absolute = Math.abs(seconds);
    const units = [
        ['year', 31_536_000],
        ['month', 2_592_000],
        ['week', 604_800],
        ['day', 86_400],
        ['hour', 3_600],
        ['minute', 60],
    ];
    const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    for (const [unit, size] of units) {
        if (absolute >= size) {
            return formatter.format(Math.round(seconds / size), unit);
        }
    }
    return formatter.format(seconds, 'second');
};
//# sourceMappingURL=format.js.map